import React from 'react';
import { Button, Result } from 'antd';
import { node, string } from 'prop-types';

/**
 * The last line of defence against a blank page.
 *
 * A render error anywhere below this unmounts the whole React tree, and what
 * the person sees is white. That is what happened in production when the
 * activity drawer read a field the API does not have: the application
 * disappeared, and the only evidence was in the console.
 *
 * This is not a substitute for the defect being fixed. It is the difference
 * between a page that says something went wrong and offers a way out, and a
 * page that says nothing at all.
 *
 * Deliberately a class: `getDerivedStateFromError` and `componentDidCatch`
 * have no hook equivalent, and there is no plan for one.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
    this.handleRetry = this.handleRetry.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError() {
    // The error itself is deliberately not held in state: nothing renders it,
    // and keeping it invites someone to.
    return { failed: true };
  }

  componentDidCatch(error, info) {
    /**
     * Reported, never swallowed. The end-to-end suite fails any test whose
     * page logs an error, so a crash that reaches this boundary still fails
     * the build rather than being quietly absorbed by it.
     *
     * This is also the single place a monitoring service would be called
     * from, if one is ever added.
     */
    console.error('Unhandled error in the React tree', {
      message: error?.message,
      componentStack: info?.componentStack,
    });
  }

  componentDidUpdate(prevProps) {
    /**
     * Clear on navigation. Without this a single crash is permanent for the
     * session — the fallback keeps rendering wherever the user goes next, and
     * every link in the application looks broken.
     */
    if (this.state.failed && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ failed: false });
    }
  }

  handleRetry() {
    this.setState({ failed: false });
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    const { failed } = this.state;
    const { children } = this.props;

    if (!failed) return children;

    return (
      <Result
        status="500"
        title="Something went wrong"
        subTitle="This page could not be displayed. Nothing you were working on has been lost."
        extra={[
          <Button type="primary" key="retry" onClick={this.handleRetry}>
            Try again
          </Button>,
          <Button key="reload" onClick={this.handleReload}>
            Reload the page
          </Button>,
        ]}
      />
    );
  }
}

ErrorBoundary.propTypes = {
  children: node.isRequired,
  /**
   * Changing this clears a failure. The route path is the useful value: a
   * crash on one screen should not follow the user to the next.
   */
  resetKey: string,
};

ErrorBoundary.defaultProps = {
  resetKey: undefined,
};

export default ErrorBoundary;
